import { createSelector } from 'reselect';
import isObject from 'lodash/isObject';
import memoize from 'lodash/memoize';

import { toBool } from '@pressreader/types';

import { CONFIG_MODULE_NAME } from '../constants';

import { IState } from './reducer';

function getState(state: { [CONFIG_MODULE_NAME]: IState }) {
    return state[CONFIG_MODULE_NAME];
}

function getNodeValue<T>(node: Record<string, unknown>, path: string, defaultValue?: T): T | undefined {
    if (!isObject(node) || !path) {
        return defaultValue;
    }

    // Fix path for case-insensitive search
    const fixCase = (name: string) =>
        Object.keys(node).find(e => {
            return e.toLowerCase() === name.toLowerCase();
        }) || name; // In case the path contains "." or when it don't really exists in the config...

    path = fixCase(path);

    if (path in node) {
        return node[path] as T;
    }

    for (let i = path.indexOf('.'); i > 0; i = path.indexOf('.', i + 1)) {
        let key = path.substring(0, i);
        key = fixCase(key);
        if (key in node) {
            return getNodeValue<T>(node[key] as Record<string, unknown>, path.substring(i + 1), defaultValue);
        }
    }

    return defaultValue;
}

const selectConfig = createSelector(getState, state => state.config);

const selectStatus = createSelector(getState, state => state.status);

const selectGetConfigValue: (state: any) => { <T>(path: string, defaultValue: T): T; <T>(path: string): T | undefined } = createSelector(
    getState,
    state =>
        memoize(
            <T>(path: string, defaultValue?: T) => getNodeValue(state.config, path, defaultValue),
            (path: string, defaultValue: unknown) => JSON.stringify({ path, defaultValue }),
        ),
);

/**
 * Config-backed `(path, defaultValue) => boolean` reader, for use inside a `createSelector`.
 *
 * A boolean flag arrives either as a real boolean or as a `'true'`/`'false'` string, so a raw
 * lookup is unsafe — the string `'false'` reads as truthy and would switch a feature on. Binding
 * the coercion to the lookup means a call site cannot skip it. Anything that is not a boolean and
 * not a case-insensitive `'true'` is falsy, so `'1'`, `'on'` and `'yes'` read as disabled.
 *
 * The returned function's identity changes whenever the config does, so a selector built on this
 * one recomputes after every config reload.
 */
const selectGetConfigBoolValue = createSelector(
    selectGetConfigValue,
    getConfigValue =>
        (path: string, defaultValue = false): boolean =>
            toBool(getConfigValue<unknown>(path, defaultValue)),
);

const selectConfigValue: <T>(state: any, path: string, defaultValue?: T) => T = createSelector(
    getState,
    (state: unknown, path: string) => path,
    (state: unknown, path: string, defaultValue?: any) => defaultValue,
    (state, path: string, defaultValue?: any) => getNodeValue(state.config, path, defaultValue),
);

export { selectConfig, selectConfigValue, selectGetConfigBoolValue, selectGetConfigValue, selectStatus };
